/**
 * The two Convex deployments this console can talk to, and which one is
 * active.
 *
 * Resolved at runtime (not Vite build-time constants) so one build
 * serves both deployments.
 *
 * Shipping both URLs in a public bundle is not a disclosure: the prod
 * bundle already contained the prod URL, and both endpoints are public
 * — they are protected by auth, not obscurity.
 *
 * .convex.cloud is the reactive client host; .convex.site serves HTTP
 * actions. They are different hosts for the same deployment and must be
 * switched together.
 */
export type AdminEnv = 'dev' | 'prod';

export const ENVIRONMENTS: Record<
  AdminEnv,
  { convexUrl: string; apiBase: string; webOrigin: string; label: string }
> = {
  dev: {
    convexUrl: 'https://lovely-sandpiper-137.eu-west-1.convex.cloud',
    apiBase: 'https://lovely-sandpiper-137.eu-west-1.convex.site',
    webOrigin: 'https://dev.wydm.app',
    label: 'Development',
  },
  prod: {
    convexUrl: 'https://savory-sockeye-554.convex.cloud',
    apiBase: 'https://savory-sockeye-554.convex.site',
    webOrigin: 'https://wydm.app',
    label: 'Production',
  },
};

export const ENV_STORAGE_KEY = 'wydm.admin.env';

/**
 * Active environment. Defaults to dev, and anything unrecognised also
 * reads as dev: if this value is absent or corrupt the console must land
 * somewhere harmless. Never fail toward prod.
 */
export function getActiveEnv(): AdminEnv {
  try {
    const raw = localStorage.getItem(ENV_STORAGE_KEY);
    return raw === 'prod' ? 'prod' : 'dev';
  } catch {
    return 'dev';
  }
}

export function setActiveEnv(env: AdminEnv): void {
  localStorage.setItem(ENV_STORAGE_KEY, env);
}

export function activeConfig() {
  return ENVIRONMENTS[getActiveEnv()];
}

// Session storage keys, namespaced per environment: the unified console
// holds a live dev session and a live prod session at the same time, and
// a shared key would mean signing into one silently clobbers the other.
//
// The old unprefixed keys (wydm.admin.token etc.) are deliberately not
// migrated — the issuer change in the RS256 migration killed those
// tokens anyway.
export const tokenKeyFor = (env: AdminEnv) => `wydm.admin.${env}.token`;
export const expKeyFor = (env: AdminEnv) => `wydm.admin.${env}.expires`;
export const meKeyFor = (env: AdminEnv) => `wydm.admin.${env}.me`;

// One-shot UI notice, not session state: set by SignIn when a fresh
// login authenticated fine against prod but the dev federation call
// failed, so the dashboard can surface it exactly once after the reload
// that lands the admin in prod (see SignIn.tsx / convex.ts's
// module-scope-client comment for why that reload is required).
//
// sessionStorage, not localStorage: this flag must not outlive the tab
// that set it, let alone survive across logins the way session state
// does. Deliberately kept out of adminSession (api.ts) — it is not a
// credential and has no per-environment shape.
export const DEV_FEDERATION_FAILED_KEY = 'wydm.admin.devFederationFailedNotice';
